import React, { useCallback, useMemo, useState } from 'react';

import { getPromoCampaignBenefitLabel } from '~/lib/benefits/promoCampaignBenefits';
import dayjs from '~/timeInit';

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  GroupAdd as GroupAddIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { DataGridPro, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid-pro';

import { useDeletePromoCampaign, useSearchPromoCampaigns } from '~/hooks/apis/promoCampaigns';

import { luxLoyaltyRegions, readPromoCampaign } from '~/services/LuxLoyaltyService';

import {
  PROMO_CAMPAIGN_BENEFIT_CODES,
  PROMO_CAMPAIGN_REGIONS,
  type PromoCampaignPresenter,
  type PromoCampaignSearchParams,
} from '~/types/loyaltyContract';

import CreatePromoCampaignDialog from './CreatePromoCampaignDialog';
import PromoCampaignAddUsersDialog from './PromoCampaignAddUsersDialog';
import PromoCampaignDetailsDialog from './PromoCampaignDetailsDialog';
import PromoCampaignToolbar from './PromoCampaignToolbar';
import UpdatePromoCampaignDialog from './UpdatePromoCampaignDialog';

const DEFAULT_PAGE_SIZE = 25;

export default function LoyaltyPromoCampaignsPage() {
  const theme = useTheme();
  const themeColor = theme.palette.primary.main;

  const deleteMutation = useDeletePromoCampaign();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [regionInput, setRegionInput] = useState<PromoCampaignSearchParams['region'] | ''>('');
  const [benefitInput, setBenefitInput] = useState<PromoCampaignSearchParams['benefitCode'] | ''>('');

  const [filters, setFilters] = useState<PromoCampaignSearchParams>({
    region: undefined,
    benefitCode: undefined,
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
  });

  const [campaignIdInput, setCampaignIdInput] = useState('');
  const [campaignLookupError, setCampaignLookupError] = useState<string | null>(null);
  const [isLookupLoading, setIsLookupLoading] = useState(false);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isAddUsersOpen, setIsAddUsersOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<PromoCampaignPresenter | null>(null);

  const queryParams: PromoCampaignSearchParams = useMemo(
    () => ({
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      region: filters.region,
      benefitCode: filters.benefitCode,
    }),
    [paginationModel, filters],
  );

  const { data, isLoading, error } = useSearchPromoCampaigns(queryParams);

  const campaigns: Array<PromoCampaignPresenter> = data?.result?.promoCampaigns || [];
  const pageInfo = data?.result?.pageInfo;
  const totalCount = pageInfo?.totalRecords ?? 0;

  const handleSearch = useCallback(() => {
    setFilters({
      region: regionInput || undefined,
      benefitCode: benefitInput || undefined,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [regionInput, benefitInput]);

  const handleClearFilters = useCallback(() => {
    setRegionInput('');
    setBenefitInput('');
    setFilters({
      region: undefined,
      benefitCode: undefined,
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    });
    setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  }, []);

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) {
        return prev;
      }
      return model;
    });
  }, []);

  const handleOpenById = useCallback(async () => {
    const value = campaignIdInput.trim();
    if (!value) {
      setCampaignLookupError('Enter a campaign ID to search');
      return;
    }
    setCampaignLookupError(null);
    setIsLookupLoading(true);
    try {
      const response = await readPromoCampaign(value);
      setSelectedCampaign(response.result.promoCampaign);
      setIsDetailsOpen(true);
    } catch (err: any) {
      setCampaignLookupError(err.message || 'Campaign not found');
    } finally {
      setIsLookupLoading(false);
    }
  }, [campaignIdInput]);

  const handleOpenDetails = useCallback((campaign: PromoCampaignPresenter) => {
    setSelectedCampaign(campaign);
    setIsDetailsOpen(true);
  }, []);

  const handleOpenUpdate = useCallback((campaign: PromoCampaignPresenter) => {
    setSelectedCampaign(campaign);
    setIsUpdateOpen(true);
  }, []);

  const handleOpenAddUsers = useCallback((campaign: PromoCampaignPresenter) => {
    setSelectedCampaign(campaign);
    setIsAddUsersOpen(true);
  }, []);

  const handleOpenDeleteConfirm = useCallback((campaign: PromoCampaignPresenter) => {
    setSelectedCampaign(campaign);
    setIsDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedCampaign) {
      return;
    }

    await deleteMutation.mutateAsync({ promoCampaignId: selectedCampaign.id });
    setIsDeleteConfirmOpen(false);
  }, [selectedCampaign, deleteMutation]);

  const getStatusChip = (campaign: PromoCampaignPresenter) => {
    if (campaign.deletedAt) {
      return <Chip label="Deleted" size="small" color="error" />;
    }

    const now = dayjs();
    const starts = dayjs(campaign.startDate);
    const ends = dayjs(campaign.endDate);

    if (ends.isBefore(now)) {
      return <Chip label="Expired" size="small" />;
    }
    if (starts.isAfter(now)) {
      return <Chip label="Scheduled" size="small" color="info" />;
    }
    return <Chip label="Active" size="small" color="success" />;
  };

  const columns: Array<GridColDef<PromoCampaignPresenter>> = useMemo(
    () => [
      {
        field: 'details',
        headerName: 'Details',
        width: 80,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: GridRenderCellParams<PromoCampaignPresenter>) => (
          <Tooltip title="View details">
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                handleOpenDetails(row);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
      {
        field: 'id',
        headerName: 'ID',
        width: 220,
      },
      {
        field: 'name',
        headerName: 'Name',
        width: 220,
      },
      {
        field: 'region',
        headerName: 'Region',
        width: 140,
        valueFormatter: (value) => luxLoyaltyRegions[value as PromoCampaignPresenter['region']] || value,
      },
      {
        field: 'benefit',
        headerName: 'Benefit',
        width: 200,
        valueFormatter: (value) => getPromoCampaignBenefitLabel(value as PromoCampaignPresenter['benefit']),
      },
      {
        field: 'tiers',
        headerName: 'Tiers',
        width: 180,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {row.tiers?.map((tier) => (
              <Chip key={tier} label={tier.charAt(0).toUpperCase() + tier.slice(1)} size="small" variant="outlined" />
            ))}
          </Stack>
        ),
      },
      {
        field: 'quantity',
        headerName: 'Quantity',
        width: 120,
        type: 'number',
      },
      {
        field: 'assignedUsersCount',
        headerName: 'Assigned Users',
        width: 140,
        type: 'number',
      },
      {
        field: 'startDate',
        headerName: 'Start Date',
        width: 130,
        valueFormatter: (value) => dayjs(value as string).format('DD/MM/YYYY'),
      },
      {
        field: 'endDate',
        headerName: 'End Date',
        width: 130,
        valueFormatter: (value) => dayjs(value as string).format('DD/MM/YYYY'),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        sortable: false,
        renderCell: ({ row }) => getStatusChip(row),
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 140,
        valueFormatter: (value) => dayjs(value as string).format('DD/MM/YYYY'),
      },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        width: 140,
        valueFormatter: (value) => dayjs(value as string).format('DD/MM/YYYY'),
      },
      {
        field: 'actions',
        headerName: '',
        width: 160,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const isDeleted = Boolean(row.deletedAt);
          return (
            <Stack direction="row" spacing={1}>
              <Tooltip title={isDeleted ? 'Cannot edit deleted campaign' : 'Edit'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={isDeleted}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenUpdate(row);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isDeleted ? 'Cannot add users to deleted campaign' : 'Add users'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={isDeleted}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenAddUsers(row);
                    }}
                  >
                    <GroupAddIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isDeleted ? 'Already deleted' : 'Delete'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={isDeleted}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenDeleteConfirm(row);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [handleOpenDetails, handleOpenUpdate, handleOpenAddUsers, handleOpenDeleteConfirm],
  );

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={1}
        sx={{
          p: 4,
          mb: 4,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography
              variant="h4"
              sx={{
                mb: 0.5,
                fontWeight: theme.typography.fontWeightBold,
                background: `linear-gradient(135deg, ${themeColor}, ${alpha(themeColor, 0.7)})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Promo Campaigns
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and manage loyalty promo campaign assignments
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreateOpen(true)}>
            Create Campaign
          </Button>
        </Box>
      </Paper>

      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              label="Region"
              value={regionInput}
              onChange={(event) => setRegionInput(event.target.value as PromoCampaignSearchParams['region'] | '')}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All Regions</MenuItem>
              {PROMO_CAMPAIGN_REGIONS.map((region) => (
                <MenuItem key={region} value={region}>
                  {luxLoyaltyRegions[region]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Benefit"
              value={benefitInput}
              onChange={(event) => setBenefitInput(event.target.value as PromoCampaignSearchParams['benefitCode'] | '')}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All Benefits</MenuItem>
              {PROMO_CAMPAIGN_BENEFIT_CODES.map((code) => (
                <MenuItem key={code} value={code}>
                  {getPromoCampaignBenefitLabel(code)}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="contained" onClick={handleSearch}>
                Search
              </Button>
              <Button variant="text" onClick={handleClearFilters}>
                Clear
              </Button>
            </Stack>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
            <TextField
              label="Go to Campaign ID"
              value={campaignIdInput}
              onChange={(event) => setCampaignIdInput(event.target.value)}
              sx={{ flex: 1 }}
            />
            <Button variant="outlined" onClick={handleOpenById} disabled={isLookupLoading}>
              Open
            </Button>
            {campaignLookupError && (
              <Typography variant="caption" color="error">
                {campaignLookupError}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Box mb={3}>
          <Alert severity="error">
            Error loading promo campaigns: {error instanceof Error ? error.message : String(error)}
          </Alert>
        </Box>
      )}

      <Paper elevation={0} sx={{ height: 650 }}>
        <DataGridPro
          rows={campaigns}
          columns={columns}
          loading={isLoading}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={totalCount}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          slots={{ toolbar: PromoCampaignToolbar }}
          sx={{
            height: '100%',
            '& .MuiDataGrid-row:hover': {
              bgcolor: alpha(themeColor, 0.08),
            },
          }}
        />
      </Paper>

      <CreatePromoCampaignDialog
        open={isCreateOpen}
        onClose={(result) => {
          setIsCreateOpen(false);
          if (result?.campaign) {
            setSelectedCampaign(result.campaign);
            if (result.openAddUsers) {
              setIsAddUsersOpen(true);
            }
          }
        }}
      />

      <UpdatePromoCampaignDialog
        open={isUpdateOpen}
        campaign={selectedCampaign}
        onClose={(updatedCampaign) => {
          setIsUpdateOpen(false);
          if (updatedCampaign) {
            setSelectedCampaign(updatedCampaign);
          }
        }}
      />

      <PromoCampaignDetailsDialog
        open={isDetailsOpen}
        campaign={selectedCampaign}
        onClose={() => setIsDetailsOpen(false)}
      />

      <PromoCampaignAddUsersDialog
        open={isAddUsersOpen}
        campaign={selectedCampaign}
        onClose={() => setIsAddUsersOpen(false)}
      />

      <Dialog open={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Campaign</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the campaign "{selectedCampaign?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setIsDeleteConfirmOpen(false)} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
